// src/features/audittrail/VerifyTab.tsx
// Prove the hash chain hasn't been tampered with. Chains are per-tenant with
// no ListChains RPC, so the chain id defaults to the current tenant and can
// be overridden. GetChainStatus, VerifyChainIntegrity.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, Badge, Card, CardContent, ErrorAlert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import type { AuditChain, VerifyChainIntegrityResponse } from '@core/types/audittrail';
import { fmtDateTime, errMessage } from './logic';

export const VerifyTab = observer(() => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const [chainId, setChainId] = useState(scope.tenantId);
  const [chain, setChain] = useState<AuditChain | null>(null);
  const [result, setResult] = useState<VerifyChainIntegrityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    if (!chainId.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    audittrailStore.GetChainStatus(chainId.trim())
      .then((res) => setChain(res.chain ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('audittrail.verify.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async () => {
    if (!chainId.trim()) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await audittrailStore.VerifyChainIntegrity(chainId.trim(), { chain: chain?.entity ?? { type_id: '', type_name: 'audit_chain', id: chainId.trim(), platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId } });
      setResult(res);
      setChain(res.chain ?? chain);
    } catch (e) {
      setError(errMessage(e, t('audittrail.verify.verifyError')));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Input label={t('audittrail.verify.chainId')} value={chainId} onChange={(e) => setChainId(e.target.value)} className="w-72" fullWidth={false} />
        <Button size="sm" variant="secondary" onClick={load} isLoading={loading}>{t('audittrail.verify.load')}</Button>
        <Button size="sm" onClick={verify} isLoading={verifying} disabled={!chainId.trim()}>{t('audittrail.verify.verifyNow')}</Button>
      </div>

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {chain && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{t('audittrail.verify.status')}</p>
              <Badge variant={chain.is_intact ? 'success' : 'error'}>
                {chain.is_intact ? t('audittrail.verify.intact') : t('audittrail.verify.tampered')}
              </Badge>
            </div>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-muted-foreground">{t('audittrail.verify.chainLength')}</dt><dd className="text-foreground">{chain.chain_length}</dd></div>
              <div><dt className="text-muted-foreground">{t('audittrail.verify.lastSequence')}</dt><dd className="text-foreground">{chain.last_sequence_number}</dd></div>
              <div><dt className="text-muted-foreground">{t('audittrail.verify.lastVerified')}</dt><dd className="text-foreground">{fmtDateTime(chain.last_verified_at as unknown as string, locale)}</dd></div>
              <div><dt className="text-muted-foreground">{t('audittrail.verify.rootHash')}</dt><dd className="break-all font-mono text-xs text-foreground">{chain.root_hash || '—'}</dd></div>
            </dl>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-sm font-medium text-foreground">{t('audittrail.verify.result')}</p>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <div><dt className="text-muted-foreground">{t('audittrail.verify.recordsVerified')}</dt><dd className="text-foreground">{result.records_verified}</dd></div>
              <div><dt className="text-muted-foreground">{t('audittrail.verify.recordsFailed')}</dt><dd className={result.records_failed > 0 ? 'text-destructive' : 'text-foreground'}>{result.records_failed}</dd></div>
            </dl>
            {result.failed_sequence_numbers.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('audittrail.verify.failedSequences')}</p>
                <p className="mt-1 font-mono text-xs text-destructive">{result.failed_sequence_numbers.join(', ')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default VerifyTab;
